import { useCallback, useContext } from 'react';
import { useTelemetryViewport, UseTelemetryViewportReturn } from '../../../../../CommonComponents/telemetry';
import {
  mergeAndSortTelemetryData,
  setGlobalSimulationStartTimeMs,
  updateGlobalTelemetrySeriesStore,
} from '../../../../../CommonComponents/telemetry/utils/telemetry-helpers.utils';
import useScenario from '../../../../hooks/useScenario';
import useUrlParams from '../../../../hooks/useUrlParams';
import { CommandCenterStateProviderContext } from '../../../../context';
import TelemetryService from '../../../../../../service/TelemetryService';

export type ViewportState = import('../../../../../CommonComponents/telemetry').TelemetryViewportState;

export function useViewport(timeframeArg?: number): UseTelemetryViewportReturn {
  const context = useContext(CommandCenterStateProviderContext);
  const { scenario } = useScenario();
  const { sat_id } = useUrlParams();

  const setTelemetrySeries = context?.setTelemetrySeries;
  const scenarioDeploymentDetails = context?.scenarioDeploymentDetails;
  const timeframe = timeframeArg ?? context?.telemetryTimeframe ?? 60;
  const customStartTimeSec = context?.telemetryCustomStartTime ?? null;

  const selectedSatellite = scenarioDeploymentDetails?.satellites?.find((sat) => sat.satelliteId === sat_id);
  const activeTrueTwinId = selectedSatellite?.trueTwinId || scenarioDeploymentDetails?.satellites?.[0]?.trueTwinId;

  // Convert simulationStartTime from scenarioDeploymentDetails or scenario to milliseconds safely
  const getSimStartTimeMs = useCallback((): number | null => {
    const raw = scenarioDeploymentDetails?.simulationStartTime ?? scenario?.simulationStartTime;
    if (!raw) return null;
    const num = Number(raw);
    if (!num || isNaN(num) || num <= 0) return null;
    const ms = num < 2e9 ? num * 1000 : num;
    setGlobalSimulationStartTimeMs(ms);
    return ms;
  }, [scenarioDeploymentDetails?.simulationStartTime, scenario?.simulationStartTime]);

  // Compute reference timestamp prioritizing in-memory data, simulationTimeAbs from deployment info, or Date.now()
  const getReferenceTs = useCallback((maxFoundTs: number): number => {
    if (maxFoundTs > 0) return maxFoundTs;

    const selectedSat = scenarioDeploymentDetails?.satellites?.find((sat) => sat.satelliteId === sat_id);
    const simTimeAbsSec = selectedSat?.simulationTimeAbs || scenarioDeploymentDetails?.satellites?.[0]?.simulationTimeAbs;
    if (simTimeAbsSec && simTimeAbsSec > 0) {
      return simTimeAbsSec < 2e9 ? simTimeAbsSec * 1000 : simTimeAbsSec;
    }

    return Date.now();
  }, [scenarioDeploymentDetails, sat_id]);

  // Historical API fetch handler
  const handleFetchHistory = useCallback(
    async (startSec: number, endSec: number) => {
      if (!activeTrueTwinId) return;

      try {
        const res = await TelemetryService.getAllPanelsTelemetryData(activeTrueTwinId, {
          startTime: startSec,
          endTime: endSec,
        });

        let fetchedSeriesList: any[] = [];
        if (Array.isArray(res)) {
          fetchedSeriesList = res;
        } else if (res && typeof res === 'object') {
          if ((res as any).id && Array.isArray((res as any).data)) {
            fetchedSeriesList = [res];
          } else if (Array.isArray((res as any).data)) {
            fetchedSeriesList = (res as any).data;
          }
        }

        if (fetchedSeriesList.length > 0 && setTelemetrySeries) {
          const normalizeId = (id?: string) => (id ? id.replace(/[\.\s]/g, ':').toLowerCase() : '');

          setTelemetrySeries((prevSeries) => {
            const currentList = Array.isArray(prevSeries) ? [...prevSeries] : [];
            const matchedFetchedIndices = new Set<number>();

            const updatedList = currentList.map((s) => {
              if (!s || !s.id) return s;
              const sNorm = normalizeId(s.id);

              const fetchedIdx = fetchedSeriesList.findIndex((item) => {
                if (!item || !item.id) return false;
                if (item.id === s.id) return true;
                if (item.id.toLowerCase() === s.id.toLowerCase()) return true;
                return normalizeId(item.id) === sNorm;
              });

              if (fetchedIdx >= 0) {
                matchedFetchedIndices.add(fetchedIdx);
                const fetched = fetchedSeriesList[fetchedIdx];
                if (fetched && Array.isArray(fetched.data)) {
                  // Buffer up to 50,000 points to prevent 24h historical truncation
                  const merged = mergeAndSortTelemetryData(s?.data || [], fetched?.data || [], 50000);
                  return { ...s, data: merged };
                }
              }
              return s;
            });

            // Append any series returned by backend that were not in prevSeries
            fetchedSeriesList.forEach((item, idx) => {
              if (!matchedFetchedIndices.has(idx) && item && item.id && Array.isArray(item.data) && item.data.length > 0) {
                const normalizedData = mergeAndSortTelemetryData([], item.data, 50000);
                updatedList.push({ ...item, data: normalizedData });
              }
            });

            updateGlobalTelemetrySeriesStore(updatedList);
            return updatedList;
          });
        }
      } catch (err) {
        console.warn("Historical telemetry fetch error:", err);
      }
    },
    [activeTrueTwinId, setTelemetrySeries]
  );

  return useTelemetryViewport({
    timeframe,
    customStartTimeSec,
    onCustomStartTimeChange: context?.setTelemetryCustomStartTime,
    viewport: context?.telemetryViewport,
    onViewportChange: context?.setTelemetryViewport,
    getSimStartTimeMs,
    getReferenceTs,
    onFetchHistory: handleFetchHistory,
    isHistoryFetchDisabled: !activeTrueTwinId,
  });
}

export default useViewport;
